import { useDeleteGroup, useMyGroup } from '@echo/core/react';
import { Button } from '@echo/ui-web';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ToolbarBack } from '@/components/empty-state-frame';
import { GroupActionFailed } from '@/components/group-load-state';
import { useGoBack } from '@/lib/use-go-back';

export const Route = createFileRoute('/_app/account_/groups_/$id_/delete')({
  component: DeleteGroupPage,
});

/**
 * Manage group › Delete group: what deleting means, then the destructive "Delete group" or
 * Cancel. Deleting takes the group away from everyone in it and can't be undone, so it is never
 * one click from Manage group. Once deleted the group is no longer one of yours: back to My
 * groups.
 */
function DeleteGroupPage() {
  const { t } = useLingui();
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const goBack = useGoBack(
    () => void navigate({ to: '/account/groups/$id', params: { id }, replace: true }),
  );
  const { group } = useMyGroup(id);
  const deleting = useDeleteGroup();
  const toolbar = (
    <ToolbarBack
      render={({ className, children }) => (
        <button type="button" className={className} onClick={goBack}>
          {children}
        </button>
      )}
    />
  );
  if (!group) return toolbar;

  const groupName = group.name;
  const cancel = goBack;
  const remove = () => {
    if (deleting.isPending) return;
    deleting.mutate(id, {
      onSuccess: () => void navigate({ to: '/account/groups', replace: true }),
    });
  };
  return (
    <>
      {toolbar}
      <h1 className="text-display-sm font-bold">
        <Trans>Delete group</Trans>
      </h1>
      <div className="flex flex-col gap-content-md pt-content-md">
        <p className="text-body-lg text-on-surface-medium">
          <Trans>
            Deleting {groupName} removes the group for everyone in it. This can't be undone, and the
            group can't be recovered.
          </Trans>
        </p>
        {deleting.isError ? <GroupActionFailed /> : null}
        <div className="flex flex-wrap justify-end gap-content-sm">
          <Button variant="secondary" onClick={cancel}>
            {t`Cancel`}
          </Button>
          <Button variant="destructive" loading={deleting.isPending} onClick={remove}>
            {t`Delete group`}
          </Button>
        </div>
      </div>
    </>
  );
}
